import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { motion, AnimatePresence } from "framer-motion";
import DiscIntro from "@/components/disc/DiscIntro";
import DiscQuestion from "@/components/disc/DiscQuestion";
import DiscResult from "@/components/disc/DiscResult";
import { QUESTIONS } from "@/components/disc/discData";
import { calculateDiscResult } from "@/components/disc/discAlgorithm";

// Fisher-Yates shuffle — randomizes question order each test
function shuffleArray(arr) {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export default function DiscTest() {
  const [stage, setStage] = useState("loading"); // loading | intro | test | result
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);
  const [shuffledQuestions, setShuffledQuestions] = useState(QUESTIONS);

  useEffect(() => {
    const timeout = setTimeout(() => setStage((s) => s === "loading" ? "intro" : s), 5000);

    db.auth.me().then(async (u) => {
      setUser(u);
      if (u) {
        const profiles = await db.entities.UserProfile.filter({ user_email: u.email }, "-created_date", 1);
        const p = profiles[0];
        if (p?.disc_completed && p?.disc_answers) {
          // Check if answers are in new Likert format (numeric values)
          // Old format had { most, least } objects — skip those and show intro to retake
          const firstValue = Object.values(p.disc_answers)[0];
          if (typeof firstValue === "number") {
            const computed = calculateDiscResult(p.disc_answers);
            setResult(computed);
            setAnswers(p.disc_answers);

            // Backfill: testes antigos não tinham disc_completed_at
            let savedAt = p.disc_completed_at;
            if (!savedAt) {
              savedAt = new Date().toISOString();
              db.entities.UserProfile.update(p.id, { disc_completed_at: savedAt }).catch(() => {});
            }
            setCompletedAt(savedAt);

            setStage("result");
            return;
          }
        }
      }
      setStage("intro");
    }).catch(() => setStage("intro"))
      .finally(() => clearTimeout(timeout));
  }, []);

  function startTest() {
    setShuffledQuestions(shuffleArray(QUESTIONS));
    setStage("test");
    setCurrentQ(0);
    setAnswers({});
  }

  function handleAnswer(questionId, value) {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    if (currentQ < shuffledQuestions.length - 1) {
      setTimeout(() => setCurrentQ(c => c + 1), 300);
    } else {
      finishTest(newAnswers);
    }
  }

  async function finishTest(finalAnswers) {
    const computed = calculateDiscResult(finalAnswers);
    setResult(computed);
    setStage("result");

    const now = new Date().toISOString();
    setCompletedAt(now);

    if (user) {
      setSaving(true);
      try {
        const existing = await db.entities.UserProfile.filter({ user_email: user.email }, "-created_date", 1);
        const coreData = {
          user_email: user.email,
          disc_completed: true,
          disc_result: computed.combinedProfile,
          disc_answers: finalAnswers,
          disc_scores: computed.percentages,
        };
        const id = existing[0]?.id;
        if (id) {
          await db.entities.UserProfile.update(id, coreData);
        } else {
          await db.entities.UserProfile.create(coreData);
        }
        // Timestamp separado — só funciona se a migração 018 já rodou
        if (id) {
          db.entities.UserProfile.update(id, { disc_completed_at: now }).catch(() => {});
        }
      } catch {
        // Garante que o resultado aparece mesmo se o save falhar
      } finally {
        setSaving(false);
      }
    }
  }

  function handleRetake() {
    setStage("intro");
    setAnswers({});
    setResult(null);
  }

  if (stage === "loading") {
    return (
      <div className="flex items-center justify-center py-20">
        <div
          className="w-6 h-6 border-2 rounded-full animate-spin"
          style={{ borderColor: "#E5E9EA", borderTopColor: "#00DCA4" }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <AnimatePresence mode="wait">
        {stage === "intro" && (
          <DiscIntro key="intro" onStart={startTest} />
        )}
        {stage === "test" && (
          <DiscQuestion
            key={`test-${currentQ}`}
            question={shuffledQuestions[currentQ]}
            questionIndex={currentQ}
            total={shuffledQuestions.length}
            onAnswer={handleAnswer}
          />
        )}
        {stage === "result" && result && (
          <DiscResult
            key="result"
            result={result}
            user={user}
            saving={saving}
            completedAt={completedAt}
            onRetake={handleRetake}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
